import { createAccessControl } from "better-auth/plugins/access";
// adminAc: built-in admin plugin statements (user, session management operations)
import { adminAc, defaultStatements } from "better-auth/plugins/admin/access";

// ── Permission Statement ──────────────────────────────────────────────────────
// Merges the built-in admin plugin statements with ImpactOT's three
// application-specific resources. This merged statement is shared by both
// server-side auth configuration and the client-side auth client instance.
//
// Sources: SRS Section 7.2 (Access Control Statement)
export const statement = {
  // Built-in: user (create, list, set-role, ban, impersonate, impersonate-admins,
  //   delete, set-password) + session (list, revoke, delete)
  ...defaultStatements,

  // Assessment lifecycle — SRS §7.2
  assessment: [
    "create", // Initiate a new assessment record (FL-015)
    "read", // View assessment list, Review Dashboard, report screen (FL-016, S-08, S-10, S-11)
    "update", // Edit wizard step data, save phase inputs, progress wizard (FE-005)
    "delete", // Hard-delete a draft assessment (FL-018)
    "archive", // Archive or unarchive an assessment (FL-017)
    "generate-report", // Trigger PDF and CSV report generation (FL-035)
  ],

  // Organisation management — SRS §7.2
  organisation: [
    "read", // View the organisation's display name in navigation and settings
    "update", // Edit the organisation's display name (FL-012)
  ],

  // Invitation management — SRS §7.2
  invitation: [
    "create", // Send an invitation email to a new user (FL-007)
    "read", // List pending invitations in S-12 alongside active user records
    "delete", // Revoke a pending invitation (FL-009)
  ],
} as const;

export const ac = createAccessControl(statement);

// ── Role Definitions ──────────────────────────────────────────────────────────
// Sources: SRS Section 7.3 (Role Definitions) and Section 7.8 (Role Hierarchy)

// Admin role
// Inherits ALL built-in admin plugin permissions via ...adminAc.statements
// (user: create, list, set-role, ban, impersonate, delete, set-password;
//  session: list, revoke, delete)
// Plus full application-level permissions on all three resources.
// Source: SRS §7.3 admin
export const adminRole = ac.newRole({
  // Inherit all built-in admin operations
  ...adminAc.statements,
  // Full assessment lifecycle
  assessment: [
    "create",
    "read",
    "update",
    "delete",
    "archive",
    "generate-report",
  ],
  // Full organisation management
  organisation: ["read", "update"],
  // Full invitation management
  invitation: ["create", "read", "delete"],
});

// User role (default)
// The primary working role — practitioners who conduct BIA work.
// Has write access to assessments but NO invitation or organisation-update perms.
// Note: delete is restricted at the application layer to own-created drafts only
//   (assessments.created_by = current_user.id AND status = 'draft') — the base
//   permission is granted here; narrowing is enforced in the server action.
// Source: SRS §7.3 user
export const userRole = ac.newRole({
  assessment: [
    "create",
    "read",
    "update",
    "delete", // App layer restricts to own drafts only
    "archive",
    "generate-report",
  ],
  organisation: ["read"],
  // No invitation permissions — accessing S-12 / S-13 returns 403
});

// Viewer role
// Read-only stakeholder access. Cannot create, modify, or delete anything.
// viewer is a CUSTOM role — not one of better-auth's two built-in roles
// ("admin" and "user"). It is explicitly defined here via ac.newRole().
// Source: SRS §7.3 viewer
export const viewerRole = ac.newRole({
  // read and generate-report are granted at the base level;
  // the application layer further restricts:
  //   - read: only complete/archived assessments (SRS §7.3 scope notes)
  //   - generate-report: only complete assessments (SRS §7.3 scope notes)
  assessment: ["read", "generate-report"],
  organisation: ["read"],
  // No invitation permissions — accessing S-12 / S-13 returns 403
});

// ── Exported roles map (passed to admin() plugin on both server and client) ───
export const roles = {
  admin: adminRole,
  user: userRole,
  viewer: viewerRole,
};

// ── Multi-role permission resolution utility ──────────────────────────────────
// Better Auth stores multiple roles as comma-separated strings (e.g. "admin,billing").
// This utility merges all role permissions and returns the union set for a resource.
export function resolveEffectivePermissions(
  userRoles: string[],
  resource: string
): string[] {
  const allActions = new Set<string>();
  for (const roleName of userRoles) {
    const role = roles[roleName as keyof typeof roles];
    if (role && resource in role.statements) {
      const actions =
        role.statements[resource as keyof typeof role.statements];
      if (Array.isArray(actions)) {
        actions.forEach((a) => allActions.add(a as string));
      }
    }
  }
  return Array.from(allActions);
}
